"use client"
import { useMutation } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { CustomerRegistrationFormData, ExtendedCustomerRegistrationErrors } from 'src/types/sign-up';
import useForm from '@utils/useForm';
import { CUSTOMER_REGISTRATION } from '@apollo-custom/mutations';
import useModal from '@apollo-custom/storage/appConfig/useModal';
import { SignupStyles } from '@styles/Signup';
import { hasAnyErrorMessage, validateFields } from '@utils/sign-up';
import SignupForm from 'app/[locale]/sign-up/SignupForm';
import Error from '@components/ErrorHandler';
import Script from 'next/script';
import { Company, RegisterCustomerInput } from 'src/generated/graphql';
import { useTranslations } from 'next-intl';

const SignUp = () => {
  const [errors, setErrors] = useState({} as ExtendedCustomerRegistrationErrors);
  const { updateValue, updateValueManually, values, populateValues } = useForm(
    {
      email: '',
      password: '',

      deliveryIsSameAsMain: true,
      deliveryContactIsSameAsMain: true,

      storeName: '',
      attention: '',
      line_1: '',
      line_2: '',
      zipCode: '',
      country: { value: '', label: '' },
      state: { value: '', label: '' },
      city: '',


      shippingEmail: '',
      shippingEmailEdited: false,
      shippingPhone: '',
      shippingPhoneEdited: false,
      shippingPhoneCountryCode: { value: '', label: '' },

      name: '',
      nameEdited: false,
      organizationNumber: '',
      billingAttention: '',
      billingLine1: '',
      billingLine2: '',
      billingCity: '',
      billingZipCode: '',
      billingState: { value: '', label: '' },
      billingCountry: { value: '', label: '' },
      billingEmail: '',
      billingEmailEdited: false,
      billingPhone: '',
      billingPhoneCountryCode: { value: '', label: '' },
      // billingPoneEdited: false,
      applicantComment: '',
      customerType: { value: '', label: '' },
      corporateID: '',
      VATNumber: '',

      invoiceIsSameAsMain: true,
      invoiceContactIsSameAsMain: true,

      invoiceAttention: '',
      invoiceLine1: '',
      invoiceLine2: '',
      invoiceZipCode: '',
      invoiceCity: '',
      invoiceCountry: { value: '', label: '' },
      invoiceState: { value: '', label: '' },
      // invoiceCustomerType: { value: '', label: '' },
      invoiceEmail: '',
      invoicePhone: '',
      invoicePhoneCountryCode: { value: '', label: '' },

      shouldSendConfirmationEmails: true,
    } as CustomerRegistrationFormData,
    setErrors,
  );


  const [step, setStep] = useState(0);
  const router = useRouter();

  const [customerRegistration, { data, error, loading }] = useMutation<{
    customerRegistration: Company,
    
  }, {input: RegisterCustomerInput}>(
    CUSTOMER_REGISTRATION
  );

  const { toggleModal, addModalData } = useModal();
  useEffect(() => {
    if (data) {
      toggleModal({
        isOpen: true
      });
      addModalData({
        message: "Your request has been sent for review. You will receive an email once your account has been approved.",
        element: () => { },
        error: '',
        props: {}
      })

      setTimeout(() => {
        toggleModal({ isOpen: false })
        router.push('/login')
      }, 3000)
    }
  }, [data])

  const t = useTranslations("Register");
  return (
    <SignupStyles>
      <Script
        strategy='beforeInteractive'
        src={`https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_API_KEY}&libraries=places`}
      ></Script>


      <h1>
        {t('header.newStoreRegistration')}
      </h1>
      <ul className="steps">
        {[
          { step: 0, label: "steps.storeType" },
          { step: 1, label: "steps.userDetails" },
          { step: 2, label: "steps.companyAddress" },
          { step: 3, label: "steps.companyContact" },
          { step: 4, label: "steps.shippingAddress" },
          { step: 5, label: "steps.shippingContact" },
          { step: 6, label: "steps.invoiceAddress" },
          { step: 7, label: "steps.invoiceContact" }
        ].map(({ step: stepIndex, label }) => (
          <li
            key={stepIndex}
            className={`text-center ${step === stepIndex ? "active" : "clickable"}`}
          >
            {t(label)}
          </li>
        ))}
      </ul>

      <form
        onSubmit={(e) => {
          e.preventDefault();

          const errors = validateFields(values, t);
          setErrors(errors);
          window.scrollTo(0, 0);

          if (!hasAnyErrorMessage(errors)) {
            delete values.shippingEmailEdited;
            delete values.billingEmailEdited;
            delete values.billingPhoneEdited;
            delete values.shippingPhoneEdited;
            delete values.nameEdited;
          }

          const copiedValues = { ...values };

          // Function to sanitize phone numbers
          const sanitizePhone = (countryCode, phone) => {
            if (!phone) return ''; // Ensure phone is not undefined or empty

            let sanitizedPhone = phone.replace(/\D/g, ''); // Remove all non-digit characters

            if (sanitizedPhone.startsWith('0')) {
              sanitizedPhone = sanitizedPhone.substring(1); // Remove leading zero
            }

            return `${countryCode}${sanitizedPhone}`;
          };

          const shippingPhone = sanitizePhone(
            copiedValues.shippingPhoneCountryCode?.value,
            copiedValues.shippingPhone
          );
          const billingPhone = sanitizePhone(
            copiedValues.billingPhoneCountryCode?.value,
            copiedValues.billingPhone
          );
          const invoicePhone = sanitizePhone(
            copiedValues.invoicePhoneCountryCode?.value,
            copiedValues.invoicePhone
          );

          // if (values.shippingPhoneCountryCode) delete values.shippingPhoneCountryCode;
          // if (values.billingPhoneCountryCode) delete values.billingPhoneCountryCode;
          // if (values.invoicePhoneCountryCode) delete values.invoicePhoneCountryCode;

          customerRegistration({
            variables: {
              input: {

                // User
                email: values.email,
                shippingPhone,
                billingPhone,
                invoicePhone,

                // Store
                storeName: values.storeName,
                attention: values.attention,
                line_1: values.line_1,
                line_2: values.line_2,
                zipCode: values.zipCode,
                city: values.city,
                country: values.country.value,
                state: values.state.value,
                shippingEmail: values.shippingEmail,

                // Billing
                name: values.name,
                organizationNumber: values.organizationNumber,
                billingAttention: values.billingAttention,
                billingLine1: values.billingLine1,
                billingLine2: values.billingLine2,
                billingCity: values.billingCity,
                billingZipCode: values.billingZipCode,
                billingCountry: values.billingCountry.value,
                billingState: values.billingState.value,
                billingEmail: values.billingEmail,
                applicantComment: values.applicantComment,
                corporateID: values.corporateID,
                VATNumber: values.VATNumber,
                customerType: values.customerType.value,


                // Invoice
                invoiceAttention: values.invoiceAttention,
                invoiceLine1: values.invoiceLine1,
                invoiceLine2: values.invoiceLine2,
                invoiceZipCode: values.invoiceZipCode,
                invoiceCity: values.invoiceCity,
                invoiceCountry: values.invoiceCountry.value,
                invoiceState: values.invoiceState.value,
                invoiceEmail: values.invoiceEmail,

                // Booleans
                shouldSendConfirmationEmails: values.shouldSendConfirmationEmails,
                deliveryIsSameAsMain: values.deliveryIsSameAsMain,
                deliveryContactIsSameAsMain: values.deliveryContactIsSameAsMain,
                invoiceIsSameAsMain: values.invoiceIsSameAsMain,
                invoiceContactIsSameAsMain: values.invoiceContactIsSameAsMain,

              
              },
            },
          });
        }}
      >
        {error ? <Error message={error?.message} /> : <></>}

        <SignupForm
          step={step}
          setStep={setStep}
          customerDetails={values}
          values={values}
          populateValues={populateValues}
          updateValue={updateValue}
          // @ts-ignore
          validateFields={validateFields}
          errors={errors}
          setErrors={setErrors}
          updateValueManually={updateValueManually}
        />
      </form>
    </SignupStyles>
  );
};

export default SignUp;
